class PdpCarouselGl {
  #slideEls;
  #slides;
  #textures = [];
  #texturesReady = [];
  #texturesScheduled = [];
  #ratios = [];
  #canvasAspect = 1.0;
  #scrollFrac = 0;
  #jumpStart = -1;
  #jumpTarget = -1;
  #canvasVisible = false;
  #dirty = false;
  #rafId = null;
  #mqReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #canvas;
  #gl;
  #program;
  #uMix;
  #uRatioA;
  #uRatioB;
  #uCanvasAspect;
  #controller;
  #resizeObserver;

  constructor(carousel) {
    this.#slideEls = Array.from(carousel.querySelectorAll('.pdp-media-carousel__slide'));
    this.#slides = carousel.querySelector('.pdp-media-carousel__slides');

    this.#canvas = document.createElement('canvas');
    this.#canvas.className = 'pdp-media-carousel__gl-canvas';
    this.#canvas.setAttribute('aria-hidden', 'true');
    carousel.appendChild(this.#canvas);

    const gl = this.#canvas.getContext('webgl') || this.#canvas.getContext('experimental-webgl');
    if (!gl) return;
    this.#gl = gl;

    if (!this.#initShaders()) return;
    this.#initGeometry();
    this.#loadTextures();
    this.#bindEvents();
    this.#onResize();
  }

  #initShaders() {
    const gl = this.#gl;

    const vs = this.#compileShader(gl.VERTEX_SHADER, `
      attribute vec2 a_position;
      varying vec2 v_uv;
      void main() {
        v_uv = vec2(a_position.x * 0.5 + 0.5, 1.0 - (a_position.y * 0.5 + 0.5));
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `);

    const fs = this.#compileShader(gl.FRAGMENT_SHADER, `
      precision mediump float;
      uniform sampler2D u_texA;
      uniform sampler2D u_texB;
      uniform float u_mix;
      uniform float u_ratioA;
      uniform float u_ratioB;
      uniform float u_canvasAspect;
      varying vec2 v_uv;

      vec2 coverUv(vec2 uv, float imgRatio, float canvasRatio) {
        float s = (imgRatio > canvasRatio)
          ? canvasRatio / imgRatio
          : imgRatio / canvasRatio;
        vec2 scale = (imgRatio > canvasRatio)
          ? vec2(s, 1.0)
          : vec2(1.0, s);
        return uv * scale + (1.0 - scale) * 0.5;
      }

      float luma(vec3 c) {
        return dot(c, vec3(0.299, 0.587, 0.114));
      }

      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }


      void main() {
        vec2 uvA = coverUv(v_uv, u_ratioA, u_canvasAspect);
        vec2 uvB = coverUv(v_uv, u_ratioB, u_canvasAspect);

        // Outgoing image slowly zooms in; incoming settles from a slight zoom
        uvA = (uvA - 0.5) * (1.0 - u_mix * 0.02) + 0.5;
        uvB = (uvB - 0.5) * (1.0 - (1.0 - u_mix) * 0.02) + 0.5;

        vec4 colorA = texture2D(u_texA, uvA);
        vec4 colorB = texture2D(u_texB, uvB);

        // Flatten transparent pixels against white so transparent PNGs composite correctly
        vec4 flatA = vec4(colorA.rgb * colorA.a + (1.0 - colorA.a), 1.0);
        vec4 flatB = vec4(colorB.rgb * colorB.a + (1.0 - colorB.a), 1.0);

        // Luminance dissolve: dark areas transition first, bright areas hold on longest.
        // Scale keeps the effect subtle; two noise octaves give the boundary organic texture.
        // Remap so the transition starts and ends sooner in the scroll arc
        // Linear remap so the transition window runs from 5% to 95% of scroll progress
        float t = clamp((u_mix - 0.05) / 0.90, 0.0, 1.0);

        // Scale lumaA so the brightest pixel's hi threshold reaches ~1.0,
        // meaning the dissolve uses the full [0,1] range with no dead zone
        float lumaA = luma(flatA.rgb) * 0.75;

        float noise = (hash(v_uv * 10.0) - 0.5) * 0.08;
        // Clamp bounds to [0,1] so dissolve is exactly 0 at u_mix=0 and exactly 1
        // at u_mix=1 for every pixel — noise offsets would otherwise leave the
        // transition fractionally incomplete at settled snap positions.
        float lo = max(0.0, lumaA + noise - 0.15);
        float hi = min(1.0, lumaA + noise + 0.15);
        float dissolve = smoothstep(lo, hi, t);

        // 0.0 = plain crossfade, 1.0 = full luma dissolve
        float blended = mix(t, dissolve, 0.8);
        gl_FragColor = mix(flatA, flatB, blended);
      }
    `);

    if (!vs || !fs) return false;

    this.#program = gl.createProgram();
    gl.attachShader(this.#program, vs);
    gl.attachShader(this.#program, fs);
    gl.linkProgram(this.#program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(this.#program, gl.LINK_STATUS)) {
      console.warn('[pdp-gl] program link failed:', gl.getProgramInfoLog(this.#program));
      return false;
    }

    gl.useProgram(this.#program);
    gl.uniform1i(gl.getUniformLocation(this.#program, 'u_texA'), 0);
    gl.uniform1i(gl.getUniformLocation(this.#program, 'u_texB'), 1);
    this.#uMix          = gl.getUniformLocation(this.#program, 'u_mix');
    this.#uRatioA       = gl.getUniformLocation(this.#program, 'u_ratioA');
    this.#uRatioB       = gl.getUniformLocation(this.#program, 'u_ratioB');
    this.#uCanvasAspect = gl.getUniformLocation(this.#program, 'u_canvasAspect');
    return true;
  }

  #compileShader(type, source) {
    const gl = this.#gl;
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.warn('[pdp-gl] shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  #initGeometry() {
    const gl = this.#gl;
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(this.#program, 'a_position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  }

  #loadTextures() {
    // Seed GL texture handles (1×1 placeholder) for all slides, but defer pixel
    // uploads until a slide is within one step of the visible position.
    const gl = this.#gl;
    for (let i = 0; i < this.#slideEls.length; i++) {
      this.#texturesReady[i] = false;
      this.#texturesScheduled[i] = false;
      const tex = gl.createTexture();
      this.#textures[i] = tex;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    }
    // Eagerly upload the first two slides so the initial view is ready.
    this.#scheduleUpload(0);
    this.#scheduleUpload(1);
  }

  #scheduleUpload(i) {
    if (i < 0 || i >= this.#slideEls.length) return;
    if (this.#texturesScheduled[i]) return;
    this.#texturesScheduled[i] = true;

    const gl = this.#gl;
    const img = this.#slideEls[i].querySelector('img.product-media__image');
    if (!img) return;

    const upload = () => {
      try {
        gl.bindTexture(gl.TEXTURE_2D, this.#textures[i]);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        this.#ratios[i] = img.naturalWidth / img.naturalHeight || 1.0;
        this.#texturesReady[i] = true;
        this.#markDirty();
      } catch {
        // Non-CORS-clean image — leave texturesReady[i] false so the canvas
        // stays hidden and the underlying <img> shows through as fallback.
      }
    };

    if (img.complete && img.naturalWidth > 0) {
      upload();
    } else {
      img.addEventListener('load', upload, { once: true });
    }
  }

  #bindEvents() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#slides.addEventListener('scroll', () => {
      const w = this.#slides.offsetWidth;
      if (w <= 0) return;
      const raw = this.#slides.scrollLeft / w;
      // Snap to integer when within a pixel of a slide boundary, preventing
      // subpixel scrollLeft values from leaving the dissolve fractionally incomplete
      const nearest = Math.round(raw);
      this.#scrollFrac = Math.abs(raw - nearest) < (1 / w) * 2 ? nearest : raw;
      // Ensure the next slide and its neighbour are uploaded before they're needed
      const active = Math.round(this.#scrollFrac);
      this.#scheduleUpload(active - 1);
      this.#scheduleUpload(active);
      this.#scheduleUpload(active + 1);
      this.#markDirty();
    }, { signal, passive: true });

    this.#resizeObserver = new ResizeObserver(() => this.#onResize());
    this.#resizeObserver.observe(this.#canvas);
  }

  #onResize() {
    const canvas = this.#canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(canvas.offsetWidth * dpr);
    const h = Math.round(canvas.offsetHeight * dpr);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      this.#gl?.viewport(0, 0, w, h);
      this.#canvasAspect = h > 0 ? w / h : 1.0;
      this.#markDirty();
    }
  }

  #markDirty() {
    if (this.#dirty) return;
    this.#dirty = true;
    this.#rafId = requestAnimationFrame(() => this.#render());
  }

  setJump(toIndex) {
    this.#jumpStart = Math.round(this.#scrollFrac);
    this.#jumpTarget = toIndex;
    this.#scheduleUpload(toIndex);
  }

  #render() {
    this.#dirty = false;
    const gl = this.#gl;
    if (!gl || !this.#program) return;

    const frac = this.#mqReducedMotion.matches ? Math.round(this.#scrollFrac) : this.#scrollFrac;

    let indexA, indexB, mixVal;
    const count = this.#slideEls.length;

    if (this.#jumpTarget >= 0) {
      // Blend directly from origin to target, ignoring intermediate slides
      const range = this.#jumpTarget - this.#jumpStart;
      const progress = range !== 0 ? (frac - this.#jumpStart) / range : 1;
      mixVal = Math.max(0, Math.min(1, progress));
      indexA = this.#jumpStart;
      indexB = this.#jumpTarget;
      // Clear once scroll has settled at the target
      if (Math.round(frac) === this.#jumpTarget && Math.abs(frac - this.#jumpTarget) < 0.01) {
        this.#jumpStart = -1;
        this.#jumpTarget = -1;
      }
    } else {
      indexA = Math.max(0, Math.min(Math.floor(frac), count - 1));
      indexB = Math.min(indexA + 1, count - 1);
      mixVal = frac - Math.floor(frac);
    }

    const readyA = this.#texturesReady[indexA];
    const readyB = this.#texturesReady[indexB];

    // Keep canvas invisible until the current slide's texture is ready,
    // so the underlying <img> shows through as a fallback during load
    const shouldShow = !!readyA;
    if (shouldShow !== this.#canvasVisible) {
      this.#canvas.style.opacity = shouldShow ? '1' : '0';
      this.#canvasVisible = shouldShow;
    }
    if (!shouldShow) return;

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.#textures[indexA]);
    gl.activeTexture(gl.TEXTURE1);
    // Fall back to texA if texB isn't ready yet (mid-scroll to an unloaded slide)
    gl.bindTexture(gl.TEXTURE_2D, readyB ? this.#textures[indexB] : this.#textures[indexA]);
    gl.uniform1f(this.#uMix,          readyB ? mixVal : 0.0);
    gl.uniform1f(this.#uRatioA,       this.#ratios[indexA] ?? 1.0);
    gl.uniform1f(this.#uRatioB,       this.#ratios[readyB ? indexB : indexA] ?? 1.0);
    gl.uniform1f(this.#uCanvasAspect, this.#canvasAspect);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  destroy() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#canvas?.remove();
    const gl = this.#gl;
    if (gl) {
      for (const tex of this.#textures) {
        if (tex) gl.deleteTexture(tex);
      }
      if (this.#program) gl.deleteProgram(this.#program);
    }
  }
}

class PdpMediaCarousel extends HTMLElement {
  #slides;
  #slideEls;
  #controls;
  #status;
  #controller;
  #observer;
  #mql;
  #gl;
  #scrollRafId = null;

  connectedCallback() {
    this.#slides = this.querySelector('.pdp-media-carousel__slides');
    this.#slideEls = Array.from(this.querySelectorAll('.pdp-media-carousel__slide'));

    if (this.#slideEls.length <= 1) return;

    this.#controls = Array.from(this.querySelectorAll('.pdp-media-carousel__control'));
    this.#status = this.querySelector('[aria-live]');
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    for (const control of this.#controls) {
      control.addEventListener('click', () => {
        this.goTo(parseInt(control.dataset.index, 10));
      }, { signal });
    }

    // POST-LAUNCH (#255): remove this block (initial staging slide scroll)
    // On initial load, scroll instantly to the variant-matched staging slide
    const initialIndex = parseInt(this.dataset.selectedStagingIndex ?? '', 10);
    if (!isNaN(initialIndex) && initialIndex > 0) {
      this.#slideEls[initialIndex]?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
      this.#setActive(initialIndex);
    }

    // POST-LAUNCH (#255): remove stagingMatch block; verify mediaId block still covers all variant-switch cases
    // On variant change, scroll to the matching slide.
    // Staging path: slides carry data-variant-id from the filename convention.
    // Standard path: slides carry data-media-id matching the variant's featured_media.
    document.addEventListener('variant:update', (event) => {
      const variantId = String(event.detail?.resource?.id ?? '');
      if (variantId) {
        const stagingMatch = this.#slideEls.find((slide) => slide.dataset.variantId === variantId);
        if (stagingMatch) {
          this.goTo(parseInt(stagingMatch.dataset.index, 10));
          return;
        }
      }
      const mediaId = String(event.detail?.resource?.featured_media?.id ?? '');
      if (mediaId) {
        const mediaMatch = this.#slideEls.find((slide) => slide.dataset.mediaId === mediaId);
        if (mediaMatch) this.goTo(parseInt(mediaMatch.dataset.index, 10));
      }
    }, { signal });

    this.#observer = new IntersectionObserver(
      (entries) => {
        let best = null;
        for (const entry of entries) {
          if (entry.isIntersecting && (!best || entry.intersectionRatio > best.intersectionRatio)) {
            best = entry;
          }
        }
        if (best) {
          this.#setActive(parseInt(best.target.dataset.index, 10));
        }
      },
      { root: this.#slides, threshold: 0.5 }
    );

    for (const slide of this.#slideEls) {
      this.#observer.observe(slide);
    }

    this.#mql = window.matchMedia('(min-width: 750px)');
    this.#onMql(this.#mql);
    this.#mql.addEventListener('change', this.#onMql, { signal });
  }

  #onMql = (e) => {
    if (e.matches) {
      if (!this.#gl) this.#gl = new PdpCarouselGl(this);
    } else {
      this.#gl?.destroy();
      this.#gl = null;
    }
  };

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#gl?.destroy();
    if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);
  }

  goTo(index) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#gl?.setJump(index);
    if (reducedMotion) {
      this.#slides.scrollLeft = this.#slideEls[index]?.offsetLeft ?? 0;
    } else {
      this.#animateScroll(this.#slideEls[index]?.offsetLeft ?? 0);
    }
  }

  #animateScroll(targetLeft, duration = 460) {
    if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);
    const el = this.#slides;
    const start = el.scrollLeft;
    const delta = targetLeft - start;
    if (delta === 0) return;
    el.style.scrollSnapType = 'none';
    const startTime = performance.now();
    const ease = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      el.scrollLeft = start + delta * ease(progress);
      if (progress < 1) {
        this.#scrollRafId = requestAnimationFrame(tick);
      } else {
        el.style.scrollSnapType = '';
        this.#scrollRafId = null;
      }
    };
    this.#scrollRafId = requestAnimationFrame(tick);
  }

  #setActive(index) {
    for (const [i, el] of this.#slideEls.entries()) {
      el.classList.toggle('is-active', i === index);
    }
    for (const [i, control] of this.#controls.entries()) {
      const active = i === index;
      control.classList.toggle('is-active', active);
      if (active) {
        control.setAttribute('aria-disabled', 'true');
      } else {
        control.removeAttribute('aria-disabled');
      }
    }
    if (this.#status) {
      this.#status.textContent = `Media ${index + 1} of ${this.#slideEls.length}`;
    }
  }
}

if (!customElements.get('pdp-media-carousel')) {
  customElements.define('pdp-media-carousel', PdpMediaCarousel);
}
